import { LegacyRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAtom, useAtomValue } from 'jotai';

import { Button } from '@/components/ui/button';
import useResizeObserver from '@/hooks/use-resize-observer';
import { cn } from '@/lib/utils';

import { selectedImageAtom, uploadedImagesAtom } from '../atoms/imageFiles';
import { editMethodAtom, settingsChangedAtom, settingsFilledAtom } from '../atoms/settings';
import { EDIT_IMAGE_METHOD } from '../constants/toggle-group-options';
import { CollapsableCard } from './collapsable-card';
import { ImagePreview } from './image-preview';
import { ImageUpload } from './image-upload';
import { MagicStyle } from './magic-style/magic-style';
import { ObjectRelationSettings } from './object-relation-settings';
import { StylePrompt } from './style-prompt';
import { StyleSettings } from './style-settings/style-settings';

interface ImageRenderingSidebarProps {
  estateId?: string;
  showMobileSettings: boolean;
  onChangeEstateId: React.Dispatch<React.SetStateAction<string | undefined>>;
  disableSelectObject: boolean;
  onUploadImage: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onStartRenderingImage: () => void;
  sidebarRef?: LegacyRef<HTMLDivElement>;
}

export const ImageRenderingSidebar = ({
  estateId,
  showMobileSettings,
  onChangeEstateId,
  disableSelectObject,
  onUploadImage,
  onStartRenderingImage,
  sidebarRef,
}: ImageRenderingSidebarProps) => {
  const uploadedImages = useAtomValue(uploadedImagesAtom);
  const selectedImageInAtom = useAtomValue(selectedImageAtom);
  const isSettingsFilled = useAtomValue(settingsFilledAtom);
  const didSettingsChange = useAtomValue(settingsChangedAtom);
  const canGenerate =
    (didSettingsChange || !selectedImageInAtom?.renderedImage) &&
    isSettingsFilled &&
    Boolean(estateId);
  const [editMethod, _setEditMethod] = useAtom(editMethodAtom);
  const { t } = useTranslation();
  const isDesktopView = useResizeObserver(1024);

  const [replacing, setReplacing] = useState(false);

  const styleSidebarSection = (
    <div ref={sidebarRef} className="flex w-full flex-col gap-[6px] sm:gap-3">
      {!replacing ? (
        <ImagePreview onReplace={() => setReplacing(true)} />
      ) : (
        <ImageUpload
          title={t('yourRoom')}
          estateId={estateId}
          onUpload={onUploadImage}
          onCancelUpload={() => setReplacing(false)}
        />
      )}
      <ObjectRelationSettings
        onChangeEstateId={onChangeEstateId}
        disableSelectObject={disableSelectObject}
      />
      <StyleSettings />
      {appConfig.stylePrompt && <StylePrompt />}
    </div>
  );

  const magicSidebarSection = (
    <div ref={sidebarRef} className="flex w-full flex-col gap-[6px] sm:gap-3">
      <MagicStyle />

      <CollapsableCard title={t('change')}></CollapsableCard>

      <CollapsableCard title={t('remove')}></CollapsableCard>
    </div>
  );

  if (uploadedImages.length === 0) {
    return (
      <div
        className={cn(
          isDesktopView ? 'min-w-[300px] max-w-[300px]' : 'w-full sm:min-w-[150px] sm:max-w-[150px]'
        )}
      >
        <ImageUpload estateId={estateId} onUpload={onUploadImage} />
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          isDesktopView
            ? 'min-w-[300px] max-w-[300px]'
            : 'w-full sm:min-w-[150px] sm:max-w-[150px]',
          showMobileSettings ? 'block' : 'hidden sm:block'
        )}
      >
        {/* NOTE: can be reenabled once we know what magic to perform 🪄 */}
        {/* <ToggleGroup */}
        {/*   className={cn( */}
        {/*     'mb-2 rounded-sm bg-text-primary p-2 shadow-lg shadow-black/10 sm:mb-6', */}
        {/*     isDesktopView ? 'min-w-[300px]' : 'min-w-[150px]', */}
        {/*     !uploadedImages.length && 'hidden sm:flex' */}
        {/*   )} */}
        {/*   type="single" */}
        {/*   disabled={!uploadedImages.length} */}
        {/*   value={editMethod} */}
        {/*   onValueChange={(value) => { */}
        {/*     if (!value || value === editMethod) return; */}
        {/*     setEditMethod(value as TYPE_EDIT_IMAGE_METHOD); */}
        {/*   }} */}
        {/*   style={{ boxShadow: '4px 4px 15px 0 rgba(0, 0, 0, 0.25)' }} */}
        {/* > */}
        {/*   {editMethodOptions.map(({ label, value, disabled }, index) => ( */}
        {/*     <ToggleGroupItem */}
        {/*       key={`edit-image-method-toggle-item-${index}`} */}
        {/*       className={cn( */}
        {/*         'h-[24px] flex-1 rounded-sm', */}
        {/*         editMethod === value ? '!bg-[#2C414B]' : '!bg-transparent', */}
        {/*         isDesktopView ? 'px-3' : 'px-1' */}
        {/*       )} */}
        {/*       value={value} */}
        {/*       disabled={disabled} */}
        {/*     > */}
        {/*       <span */}
        {/*         className={cn( */}
        {/*           'text-[17px] font-semibold', */}
        {/*           editMethod === value ? 'text-primary-light' : 'text-white' */}
        {/*         )} */}
        {/*       > */}
        {/*         {t(label)} */}
        {/*       </span> */}
        {/*     </ToggleGroupItem> */}
        {/*   ))} */}
        {/* </ToggleGroup> */}
        {editMethod === EDIT_IMAGE_METHOD.STYLE ? styleSidebarSection : magicSidebarSection}
        {editMethod ? (
          <div className="mt-4 flex w-full justify-center gap-2">
            <Button
              disabled={!canGenerate}
              className="mt-2 h-[34px] w-[120px] py-1"
              onClick={onStartRenderingImage}
            >
              {t('create')}
            </Button>
          </div>
        ) : null}
      </div>
    </>
  );
};
